import { useMemo, useState, type FormEvent } from 'react';
import { apiFetch } from '../lib/api';
import { num, useApi } from '../lib/reports';

type Role = 'admin' | 'member';
type Status = 'active' | 'pending';

interface AdminUser {
  id: string;
  email: string | null;
  full_name: string | null;
  role: Role;
  last_sign_in_at: string | null;
  created_at: string | null;
  status: Status;
}

interface AdminUsersResponse {
  users: AdminUser[];
}

const ROLE_LABEL: Record<Role, string> = { admin: 'Admin', member: 'Member' };

function formatLastSeen(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return new Intl.DateTimeFormat('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}

export function InviteUser() {
  const { data, loading, error, reload } = useApi<AdminUsersResponse>('/api/admin/users');
  const users = data?.users ?? [];

  const [statusFilter, setStatusFilter] = useState<'' | Status>('');
  const [roleFilter, setRoleFilter] = useState<'' | Role>('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (statusFilter && u.status !== statusFilter) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (q) {
        const hay = `${u.email ?? ''} ${u.full_name ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, statusFilter, roleFilter, search]);

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Team / Users</p>
          <h1>Users</h1>
          <p className="muted">Invite teammates and review who has access to the reporting portal.</p>
        </div>
      </header>

      <InviteForm onInvited={reload} />

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Users</h2>
            <p className="muted small">
              {num(filtered.length)} of {num(users.length)} shown
            </p>
          </div>
          <div className="filters filters-inline">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as '' | Status)}>
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
            </select>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value as '' | Role)}>
              <option value="">All roles</option>
              <option value="admin">Admin</option>
              <option value="member">Member</option>
            </select>
            <input
              type="search"
              placeholder="Search email or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {loading && !data && <p className="muted">Loading…</p>}

        {data && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Full name</th>
                  <th>Role</th>
                  <th>Last seen</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id}>
                    <td className="cell-strong">{u.email ?? '—'}</td>
                    <td>{u.full_name ?? '—'}</td>
                    <td>
                      <span className={`cat cat-role-${u.role}`}>{ROLE_LABEL[u.role]}</span>
                    </td>
                    <td>{formatLastSeen(u.last_sign_in_at)}</td>
                    <td>
                      <span className={`cat cat-${u.status}`}>
                        {u.status === 'active' ? 'Active' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <p className="muted">No users match the current filters.</p>}
          </div>
        )}
      </div>
    </section>
  );
}

function InviteForm({ onInvited }: { onInvited: () => void }) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setSubmitting(true);

    try {
      await apiFetch('/api/invite', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), full_name: fullName.trim(), role }),
      });
      setMessage(
        `Invitation sent to ${email}. They'll get an email with a link to set their password and sign in — no separate password reset needed.`,
      );
      setEmail('');
      setFullName('');
      setRole('member');
      onInvited();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2>Invite a user</h2>
          <p className="muted small">
            Sends a Supabase invitation email. <b>Admins</b> can manage users and invite others;{' '}
            <b>Members</b> can view the reports.
          </p>
        </div>
      </div>
      <form onSubmit={onSubmit} className="invite-form" noValidate>
        <div className="invite-fields">
          <label>
            Email
            <input
              type="email"
              required
              value={email}
              placeholder="name@company.com"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            Full name
            <input
              type="text"
              value={fullName}
              placeholder="Jane Smith"
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>
          <label>
            Role
            <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              <option value="member">Member — view reports</option>
              <option value="admin">Admin — full access + user management</option>
            </select>
          </label>
          <button type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send invite'}
          </button>
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p className="form-success" role="status">
            {message}
          </p>
        )}
      </form>
    </div>
  );
}
